import { Box, Button, FormControl, FormErrorMessage, FormLabel, Grid, GridItem, Input, Select, CloseButton } from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import MyDiv from "./familyDetail.style";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IGender } from "../../../../Admin/Master/Gender/model";
import { CustomToast } from "../../../../../components";
import { useFetch } from "../../../../../hooks/useFetch";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";

interface FamilyMember {
    employeeId?: number
    relation: number;
    fullName: string;
    gender: number;
    dob: string;
}

interface FamilyDetailForm {
    members: FamilyMember[];
}

interface familyDetailsProps {
    nextStep: () => void;
    prevStep: () => void;
    familyDetail: any;
    setFamilyDetail: React.Dispatch<React.SetStateAction<any>>;
    employeeId: number;
    setEmployeeId?: React.Dispatch<React.SetStateAction<number | null>>;
}

const schema = yup.object().shape({
    members: yup.array().of(
        yup.object().shape({
            relation: yup.number().typeError("Relation is required").required("Relation is required").min(1, "Relation is required"),
            fullName: yup.string().required("Full name is required"),
            gender: yup.number().typeError("Gender is required").required("Gender is required").min(1, "Gender is required"),
            dob: yup.string().required("Date of birth is required").test("not-in-future", "Future date not allowed", value => {
                if (!value) return false;
                const inputDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return inputDate <= today;
            }),
        })
    ).required("At least one family member is required").min(1, "At least one family member is required")
});

export const FamilyDetail = ({ nextStep, prevStep, familyDetail, setFamilyDetail, employeeId }: familyDetailsProps) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const [loading, setLoading] = useState(false);
    const [genderOptions, setGenderOptions] = useState<IGender[]>([]);
    const [relationOption, setRelationOption] = useState<any[]>([]);

    const { handleSubmit, formState: { errors }, control, trigger, setValue, reset } = useForm<FamilyDetailForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            members: [{ relation: 0, fullName: "", gender: 0, dob: "" }]
        },
        mode: "onBlur"
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "members"
    });

    useEffect(() => {
        if (familyDetail && familyDetail.members) {
            reset({
                members: familyDetail.members
            });
        }
    }, [familyDetail, reset]);

    const onSubmit = async (data: FamilyDetailForm) => {
        if (!employeeId) {
            addToast({ message: "Employee ID not found", status: "error" });
            return;
        }
        const payload = {
            employeeId: employeeId,
            members: data.members
        };
        setFamilyDetail(data);
        const response = await fetchApi("Hrlense_Employee/familyDeatails", "POST", payload, "", "");
        if (response) {
            addToast({ message: response.message, status: 'success' });
            nextStep();
        }
    };

    const loadGenderOptions = async () => {
        const res = await fetchApi("DropDown/gender", "GET", null, "", "");
        if (res) setGenderOptions(res);
    };

    const loadRelationDropDown = async () => {
        const res = await fetchApi("DropDown/relation", "GET", null, "", "");
        if (res) setRelationOption(res);
    };

    useEffect(() => {
        loadGenderOptions();
        loadRelationDropDown();
    }, []);

    const handleAddMember = async () => {
        const lastIndex = fields.length - 1;
        const isValid = await trigger(`members.${lastIndex}`);
        if (!isValid) {
            addToast({ message: "Please fill in the current member before adding a new one.", status: "error" });
            return;
        }
        append({ relation: 0, fullName: "", gender: 0, dob: "" });
    };
    const membersWatch = useWatch({ control, name: "members", });

    useEffect(() => {
        membersWatch?.forEach((m: FamilyMember, index: number) => {
            const rel = relationOption?.find((r) => r.id === m?.relation);
            if (rel) {
                if (rel.relation.toLowerCase() === "father") {
                    const male = genderOptions.find(
                        (g) => g.gender.trim().toLowerCase() === "male"
                    );
                    if (male && m.gender !== male.id) {
                        setValue(`members.${index}.gender`, male.id, { shouldValidate: true });
                    }
                } else if (rel.relation.toLowerCase() === "mother") {
                    const female = genderOptions.find(
                        (g) => g.gender.trim().toLowerCase() === "female"
                    );
                    if (female && m.gender !== female.id) {
                        setValue(`members.${index}.gender`, female.id, { shouldValidate: true });
                    }
                }
            }
        });
    }, [membersWatch, relationOption, genderOptions, setValue]);


    return (
        <MyDiv>
            <Box m={6} as="form" onSubmit={handleSubmit(onSubmit)}>
                {fields.map((field, index) => (
                    <Box key={field.id} position="relative" border="1px solid #ddd" borderRadius="md" p={4} mb={6}>
                        {fields.length > 1 && (
                            <CloseButton size="sm" color="red.500" position="absolute" top={2} right={2} onClick={() => remove(index)} />
                        )}
                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                            {/* Relation */}
                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.members?.[index]?.relation} isRequired>
                                    <FormLabel>Relation</FormLabel>
                                    <Controller
                                        name={`members.${index}.relation`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select placeholder="Select Relation" value={field.value ? field.value.toString() : ""} onChange={(e) => field.onChange(Number(e.target.value))}>
                                                {relationOption.map((option) => {
                                                    const alreadySelected = membersWatch?.some(
                                                        (m, i) => m.relation === option.id && i !== index
                                                    );
                                                    const disabled = option.relation.toLowerCase() === "father" || option.relation.toLowerCase() === "mother" ? alreadySelected : false;
                                                    return (
                                                        <option key={option.id} value={option.id.toString()} disabled={disabled} >
                                                            {option.relation}
                                                        </option>
                                                    );
                                                })}
                                            </Select>
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.members?.[index]?.relation?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            {/* Full Name */}
                            <GridItem>
                                <FormControl mb={4} isInvalid={!!errors?.members?.[index]?.fullName} isRequired >
                                    <FormLabel>Full Name</FormLabel>
                                    <Controller
                                        name={`members.${index}.fullName`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                placeholder="Enter full name"
                                                value={field.value || ""}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                                    field.onChange(val);
                                                }}
                                            />
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.members?.[index]?.fullName?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            {/* Gender */}
                            <GridItem>
                                <FormControl
                                    mb={4}
                                    isInvalid={!!errors?.members?.[index]?.gender}
                                    isRequired
                                >
                                    <FormLabel>Gender</FormLabel>
                                    <Controller
                                        name={`members.${index}.gender`}
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                placeholder="Select Gender"
                                                value={field.value ? field.value.toString() : ""}
                                                onChange={(e) => field.onChange(Number(e.target.value))}
                                            >
                                                {genderOptions.map((option) => (
                                                    <option key={option.id} value={option.id.toString()}>
                                                        {option.gender}
                                                    </option>
                                            ))}
                                            </Select>
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.members?.[index]?.gender?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>

                            {/* Date of Birth */}
                            <GridItem>
                                <FormControl
                                    mb={4}
                                    isInvalid={!!errors?.members?.[index]?.dob}
                                    isRequired
                                >
                                    <FormLabel>Date of Birth</FormLabel>
                                    <Controller
                                        name={`members.${index}.dob`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                value={field.value || ""}
                                                onChange={field.onChange}
                                                max={new Date().toISOString().split("T")[0]}
                                            />
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors?.members?.[index]?.dob?.message}
                                    </FormErrorMessage>
                                </FormControl>
                            </GridItem>
                        </Grid>

                    </Box>
                ))}
                <Button
                    colorScheme="green"
                    variant="outline"
                    onClick={handleAddMember}
                >
                    + Add More
                </Button>
                <Box display="flex" justifyContent="space-between" mt={4}>

                    <Button onClick={prevStep}  colorScheme="gray" leftIcon={<ArrowBackIcon boxSize={4} />}>
                        Previous
                    </Button>
                    <Button type="submit" isLoading={loading} colorScheme="green" rightIcon={<ArrowForwardIcon boxSize={4} />}>
                        Save & Next
                    </Button>
                </Box>
            </Box>
        </MyDiv>
    );
};
